/** @module components/product-feed-layout */
import baustein from "baustein";
import analytics from "web/script/analytics/analytics";
import rothko from "web/script/modules/rothko";
import globals from "web/script/modules/globals";
import ProductFeed from "web/script/components/product-feed";
import store from "web/redux/store";
import { setAll } from "web/redux/ducks/feed-show-more";
import { setLoading } from "web/redux/ducks/feed-sidebar";
import { watchState } from "web/redux/state-watcher";

import "web/script/components/adsense-custom-search-ads";
import "web/script/components/category-menu";
import "web/script/components/featured-banners-carousel";
import "web/script/components/feed-title";
import "web/script/components/follow-button";
import "web/script/components/product-feed-count";
import "web/script/components/product-page-suggestions";
import "web/script/components/read-more-text";
import "web/script/components/tracked-link";
import "web/script/components/ux-score";

const LANDING_CONTENT_SELECTOR = ".landing-content";

function showLandingContent(state) {
    return state.feedFiltersReducer.showLandingContent;
}

/**
 * The product feed layout controls the dynamic interactions between the Product Feed,
 * Filters and other components. We can't use a single template to define a layout for
 * product feeds, however, since they're different from page to page, so it's up to this
 * component to ensure some sanity.
 * @constructor
 * @alias module:components/product-feed-layout
 * @extends module:components/components
 */

export default baustein.register(
    "product-feed-layout",
    /** @lends module:components/product-feed-layout.prototype */
    {
        _currentRenderedPage: null,

        init() {
            this.client = new rothko.Rothko("modules/product_feed");
            this.client.on("data", this._onGetFeedData.bind(this));

            // capture the initial page this loaded on
            let parsedUrl = new URL(globals.window.location.href);
            this._currentRenderedPage = parsedUrl.pathname + parsedUrl.search;

            // TODO: should this (and a constant with the classname) live in its own module?
            watchState(showLandingContent, (show) => {
                if (show) {
                    return;
                }
                // because ie11 this can't just be `el.remove()` https://caniuse.com/#search=remove
                this.el
                    .querySelectorAll(LANDING_CONTENT_SELECTOR)
                    .forEach((el) => el.parentNode.removeChild(el));
            });
        },
        // Triggered from a simplified history router in lyst-app
        /** @param {import("web/script/utils/history").BrowserHistoryEvent} data */
        onFilterUpdate(event, data) {
            let { newURL } = data;
            return this.updateOptions({
                url: newURL.pathname + newURL.search,
            });
        },

        _onMoreLoaded() {
            const showMoreButton = this.findComponent("click-to-show-oos");
            if (showMoreButton) {
                showMoreButton.destroy();
            }
        },

        /**
         * When the options change into the component, we should load a new feed.
         *
         * Once the feed has been loaded, replace the existing one with the new one. And then
         * load the filters.
         */
        onOptionsChange() {
            const feedUrl = this.options.url;
            if (!feedUrl || this._currentRenderedPage === feedUrl) {
                return;
            }

            this._loadNewFeed(feedUrl);
            this._currentRenderedPage = feedUrl;
        },

        _loadNewFeed(feedUrl) {
            const existingFeed = this.findComponent("product-feed");
            existingFeed.startLoading();
            store.dispatch(setLoading(true));

            this.client.fetch({ url: feedUrl }).catch(() => {
                // if an error occurred then reload the page
                globals.window.location.reload();
            });
        },

        _onGetFeedData(ev, data) {
            const existingFeed = this.findComponent("product-feed");
            if (data.show_more_button) {
                store.dispatch(setAll(data.show_more_button));
            }
            // create a new feed and filters components
            const feed = new ProductFeed({
                feedData: data,
                pagination: data.pagination,
                modifier: existingFeed.options.modifier,
                infiniteScrollEnabled: existingFeed.options.infiniteScrollEnabled,
                feedSortDropdown: data.feed_sort_dropdown,
                features: data.features,
            });

            // logging how many rows the user could see, normalised based on desktop
            const itemCount = data.feed_items.length;

            analytics.event("FEED", "POST_FILTER_ROWS", Math.min(Math.ceil(itemCount / 3), 4));

            // insert the new feed, delete the old one
            requestAnimationFrame(() => {
                feed.insertBefore(existingFeed.el);
                existingFeed.destroy();
                store.dispatch(setLoading(false));
            });
        },
    }
);
